/**
 * useLlmTaskPrefill — LLM-powered auto-population of ComprehensiveTaskForm fields.
 *
 * Sends user context + TASK_FIELD_MANIFEST to a `generateTaskPrefill` GraphQL mutation,
 * then returns per-field suggestions with confidence scores for review.
 *
 * Pattern follows useLlmFormSchemaEditor (DRR-056) but is one-shot rather than draft-based.
 */

import { useCallback, useMemo, useReducer } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import type { TaskFormState } from '../enhanced/taskFormState';
import { TASK_FIELD_MANIFEST } from '../enhanced/taskFormState';

// ============================================================================
// GraphQL
// ============================================================================

const GENERATE_TASK_PREFILL = gql`
  mutation GenerateTaskPrefill($input: GenerateTaskPrefillInput!) {
    generateTaskPrefill(input: $input) {
      suggestions {
        fieldName
        suggestedValue
        confidence
        explanation
        isPii
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface TaskPrefillSuggestion {
  fieldName: string;
  suggestedValue: unknown;
  confidence: number;
  explanation: string;
  isPii: boolean;
}

export type SuggestionDecision = 'pending' | 'accepted' | 'rejected';

interface SuggestionWithDecision extends TaskPrefillSuggestion {
  decision: SuggestionDecision;
}

interface PrefillState {
  suggestions: SuggestionWithDecision[];
  isGenerating: boolean;
  error: string | null;
  contextText: string;
}

type PrefillAction =
  | { type: 'SET_CONTEXT'; text: string }
  | { type: 'GENERATE_START' }
  | { type: 'GENERATE_SUCCESS'; suggestions: TaskPrefillSuggestion[] }
  | { type: 'GENERATE_ERROR'; error: string }
  | { type: 'DECIDE'; fieldName: string; decision: SuggestionDecision }
  | { type: 'DECIDE_ALL'; decision: SuggestionDecision }
  | { type: 'ACCEPT_HIGH_CONFIDENCE'; threshold: number }
  | { type: 'CLEAR' };

const initialState: PrefillState = {
  suggestions: [],
  isGenerating: false,
  error: null,
  contextText: '',
};

function prefillReducer(state: PrefillState, action: PrefillAction): PrefillState {
  switch (action.type) {
    case 'SET_CONTEXT':
      return { ...state, contextText: action.text };

    case 'GENERATE_START':
      return { ...state, isGenerating: true, error: null, suggestions: [] };

    case 'GENERATE_SUCCESS':
      return {
        ...state,
        isGenerating: false,
        suggestions: action.suggestions.map(s => ({ ...s, decision: 'pending' as const })),
      };

    case 'GENERATE_ERROR':
      return { ...state, isGenerating: false, error: action.error };

    case 'DECIDE':
      return {
        ...state,
        suggestions: state.suggestions.map(s =>
          s.fieldName === action.fieldName ? { ...s, decision: action.decision } : s,
        ),
      };

    case 'DECIDE_ALL':
      return {
        ...state,
        suggestions: state.suggestions.map(s => ({ ...s, decision: action.decision })),
      };

    case 'ACCEPT_HIGH_CONFIDENCE':
      return {
        ...state,
        suggestions: state.suggestions.map(s =>
          s.confidence >= action.threshold && !s.isPii
            ? { ...s, decision: 'accepted' as const }
            : s,
        ),
      };

    case 'CLEAR':
      return initialState;

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export interface UseLlmTaskPrefillResult {
  state: PrefillState;
  setContextText: (text: string) => void;
  generate: () => Promise<void>;
  acceptSuggestion: (fieldName: string) => void;
  rejectSuggestion: (fieldName: string) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  acceptHighConfidence: (threshold?: number) => void;
  getAcceptedState: () => Partial<TaskFormState>;
  clear: () => void;
  /** Count of accepted suggestions */
  acceptedCount: number;
  /** Count of rejected suggestions */
  rejectedCount: number;
  /** Count of pending suggestions */
  pendingCount: number;
}

export function useLlmTaskPrefill(): UseLlmTaskPrefillResult {
  const [state, dispatch] = useReducer(prefillReducer, initialState);
  const [generateMutation] = useMutation<Record<string, any>>(GENERATE_TASK_PREFILL);

  const setContextText = useCallback((text: string) => {
    dispatch({ type: 'SET_CONTEXT', text });
  }, []);

  const generate = useCallback(async () => {
    if (!state.contextText.trim()) return;

    dispatch({ type: 'GENERATE_START' });

    try {
      const result = await generateMutation({
        variables: {
          input: {
            contextText: state.contextText,
            fieldManifest: TASK_FIELD_MANIFEST.map(f => ({
              name: f.name,
              type: f.type,
              description: f.description,
              options: f.options,
            })),
          },
        },
      });

      const suggestions: TaskPrefillSuggestion[] =
        result.data?.generateTaskPrefill?.suggestions ?? [];
      dispatch({ type: 'GENERATE_SUCCESS', suggestions });
    } catch (error) {
      dispatch({ type: 'GENERATE_ERROR', error: (error as Error).message });
    }
  }, [state.contextText, generateMutation]);

  const acceptSuggestion = useCallback((fieldName: string) => {
    dispatch({ type: 'DECIDE', fieldName, decision: 'accepted' });
  }, []);

  const rejectSuggestion = useCallback((fieldName: string) => {
    dispatch({ type: 'DECIDE', fieldName, decision: 'rejected' });
  }, []);

  const acceptAll = useCallback(() => {
    dispatch({ type: 'DECIDE_ALL', decision: 'accepted' });
  }, []);

  const rejectAll = useCallback(() => {
    dispatch({ type: 'DECIDE_ALL', decision: 'rejected' });
  }, []);

  const acceptHighConfidence = useCallback((threshold = 0.85) => {
    dispatch({ type: 'ACCEPT_HIGH_CONFIDENCE', threshold });
  }, []);

  const getAcceptedState = useCallback((): Partial<TaskFormState> => {
    const accepted: Partial<TaskFormState> = {};
    for (const s of state.suggestions) {
      if (s.decision === 'accepted') {
        (accepted as any)[s.fieldName] = s.suggestedValue;
      }
    }
    return accepted;
  }, [state.suggestions]);

  const clear = useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  const acceptedCount = useMemo(
    () => state.suggestions.filter(s => s.decision === 'accepted').length,
    [state.suggestions],
  );
  const rejectedCount = useMemo(
    () => state.suggestions.filter(s => s.decision === 'rejected').length,
    [state.suggestions],
  );
  const pendingCount = useMemo(
    () => state.suggestions.filter(s => s.decision === 'pending').length,
    [state.suggestions],
  );

  return {
    state,
    setContextText,
    generate,
    acceptSuggestion,
    rejectSuggestion,
    acceptAll,
    rejectAll,
    acceptHighConfidence,
    getAcceptedState,
    clear,
    acceptedCount,
    rejectedCount,
    pendingCount,
  };
}

export default useLlmTaskPrefill;
